import React, { useState } from 'react';
import { useAdmin } from '../../context/AdminContext';
import { adminApi } from '../../api';
import { toast } from '../../hooks/use-toast';
import { Plus, Trash2, Edit2, Key, X, Save, Image as ImageIcon } from 'lucide-react';
import ProductIcon, { ICON_REGISTRY } from '../../components/ProductIcon';
import IconPicker from '../../components/IconPicker';

const emptyForm = () => ({
  name: '', category: 'General', icon: 'openai', usdt: 0, inr: 0,
  delivery_mode: 'manual', manual_stock: 0, description: '', active: true,
});

const ProductsAdmin = () => {
  const { products, refreshAll } = useAdmin();
  const [form, setForm] = useState(null);
  const [keysFor, setKeysFor] = useState(null);
  const [keysText, setKeysText] = useState('');
  const [existingKeys, setExistingKeys] = useState([]);
  const [iconPickerOpen, setIconPickerOpen] = useState(false);

  const openNew = () => setForm({ id: null, ...emptyForm() });
  const openEdit = (p) => setForm({ ...p });
  const close = () => setForm(null);

  const save = async () => {
    try {
      const payload = {
        name: form.name, category: form.category, icon: form.icon,
        usdt: Number(form.usdt), inr: Number(form.inr),
        delivery_mode: form.delivery_mode,
        manual_stock: form.delivery_mode === 'manual' ? Number(form.manual_stock) : 0,
        description: form.description, active: !!form.active,
      };
      if (form.id) await adminApi.updateProduct(form.id, payload);
      else await adminApi.createProduct(payload);
      toast({ title: form.id ? 'Product updated' : 'Product created' });
      refreshAll();
      close();
    } catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail || 'Try again' }); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete product "${p.name}"? Its keys will be removed.`)) return;
    try {
      await adminApi.deleteProduct(p.id);
      toast({ title: 'Deleted' });
      refreshAll();
    } catch (e) { toast({ title: 'Failed' }); }
  };

  const openKeys = async (p) => {
    setKeysFor(p);
    setKeysText('');
    try { setExistingKeys(await adminApi.listKeys(p.id)); } catch { setExistingKeys([]); }
  };
  const closeKeys = () => { setKeysFor(null); setKeysText(''); setExistingKeys([]); };
  const addKeys = async () => {
    const arr = keysText.split('\n').map((s) => s.trim()).filter(Boolean);
    if (arr.length === 0) return;
    try {
      const r = await adminApi.addKeys(keysFor.id, arr);
      toast({ title: `${r.added} keys added` });
      setKeysText('');
      setExistingKeys(await adminApi.listKeys(keysFor.id));
      refreshAll();
    } catch (e) { toast({ title: 'Failed' }); }
  };

  return (
    <div className="px-8 py-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] tracking-[0.25em] text-[#5a5a5a] font-mono">PRODUCTS</div>
          <div className="text-2xl text-[#e6e6e6] font-mono">{products.length} <span className="text-[10px] text-[#5a5a5a] tracking-[0.25em]">ITEMS</span></div>
        </div>
        <button onClick={openNew} className="flex items-center gap-2 px-4 py-2 text-[11px] tracking-[0.2em] rounded-md border border-[#22c55e] text-[#22c55e] hover:bg-[#0f1a12] font-mono">
          <Plus size={13} /> NEW PRODUCT
        </button>
      </div>

      <div className="border border-[#151515] rounded-md overflow-hidden font-mono">
        <div className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr_140px] px-4 py-3 text-[10px] tracking-[0.25em] text-[#5a5a5a] border-b border-[#151515] bg-[#080808]">
          <div>NAME</div>
          <div>CATEGORY</div>
          <div>USDT</div>
          <div>INR</div>
          <div>STOCK</div>
          <div>MODE</div>
          <div className="text-right">ACTIONS</div>
        </div>
        {products.map((p) => (
          <div key={p.id} className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr_140px] px-4 py-3 text-[12px] border-b border-[#0f0f0f] last:border-0 items-center hover:bg-[#0d0d0d]">
            <div className="flex items-center gap-2 text-[#e6e6e6] uppercase truncate pr-3">
              <ProductIcon type={p.icon} size={14} />
              <span className="truncate">{p.name}</span>
              {!p.active && <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#1a1a1a] text-[#8a8a8a]">INACTIVE</span>}
            </div>
            <div className="text-[#8a8a8a]">{p.category}</div>
            <div className="text-[#e6e6e6]">${Number(p.usdt).toFixed(2)}</div>
            <div className="text-[#e6e6e6]">₹{Number(p.inr).toFixed(2)}</div>
            <div className={p.stock <= 0 ? 'text-[#ef4444]' : p.stock <= 3 ? 'text-[#f59e0b]' : 'text-[#22c55e]'}>{p.stock}</div>
            <div className="text-[#8a8a8a] text-[10px] tracking-[0.2em]">{p.delivery_mode?.toUpperCase()}</div>
            <div className="text-right flex justify-end gap-1">
              {p.delivery_mode === 'auto' && (
                <button onClick={() => openKeys(p)} title="Manage keys" className="p-1.5 rounded border border-[#1f1f1f] text-[#22c55e] hover:bg-[#0f1a12]"><Key size={12} /></button>
              )}
              <button onClick={() => openEdit(p)} className="p-1.5 rounded border border-[#1f1f1f] text-[#8a8a8a] hover:border-[#22c55e] hover:text-[#22c55e]"><Edit2 size={12} /></button>
              <button onClick={() => remove(p)} className="p-1.5 rounded border border-[#1f1f1f] text-[#8a8a8a] hover:border-[#ef4444] hover:text-[#ef4444]"><Trash2 size={12} /></button>
            </div>
          </div>
        ))}
      </div>

      {form && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6 overflow-y-auto">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={close} />
          <div className="relative w-full max-w-[560px] border border-[#1f1f1f] bg-[#0a0a0a] rounded-md p-6 font-mono my-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg text-[#e6e6e6]">{form.id ? 'Edit Product' : 'New Product'}</h2>
              <button onClick={close} className="text-[#5a5a5a] hover:text-[#e6e6e6]"><X size={16} /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="block text-[10px] tracking-[0.25em] text-[#7a7a7a] mb-1">NAME</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-transparent border border-[#1f1f1f] rounded-md px-3 py-2 text-sm outline-none focus:border-[#22c55e]" />
              </div>
              <div>
                <label className="block text-[10px] tracking-[0.25em] text-[#7a7a7a] mb-1">CATEGORY</label>
                <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full bg-transparent border border-[#1f1f1f] rounded-md px-3 py-2 text-sm outline-none focus:border-[#22c55e]" />
              </div>
              <div>
                <label className="block text-[10px] tracking-[0.25em] text-[#7a7a7a] mb-1">ICON</label>
                <button
                  type="button"
                  onClick={() => setIconPickerOpen(true)}
                  className="w-full bg-transparent border border-[#1f1f1f] rounded-md px-3 py-2 text-sm outline-none hover:border-[#22c55e] transition-colors flex items-center gap-2 text-left"
                >
                  <ProductIcon type={form.icon} size={14} />
                  <span className="flex-1 text-[#e6e6e6] truncate">
                    {ICON_REGISTRY.find((i) => i.key === form.icon)?.label || form.icon}
                  </span>
                  <ImageIcon size={12} className="text-[#5a5a5a]" />
                </button>
              </div>
              <div>
                <label className="block text-[10px] tracking-[0.25em] text-[#7a7a7a] mb-1">USDT PRICE</label>
                <input type="number" step="0.01" value={form.usdt} onChange={(e) => setForm({ ...form, usdt: e.target.value })} className="w-full bg-transparent border border-[#1f1f1f] rounded-md px-3 py-2 text-sm outline-none focus:border-[#22c55e]" />
              </div>
              <div>
                <label className="block text-[10px] tracking-[0.25em] text-[#7a7a7a] mb-1">INR PRICE</label>
                <input type="number" step="0.01" value={form.inr} onChange={(e) => setForm({ ...form, inr: e.target.value })} className="w-full bg-transparent border border-[#1f1f1f] rounded-md px-3 py-2 text-sm outline-none focus:border-[#22c55e]" />
              </div>
              <div>
                <label className="block text-[10px] tracking-[0.25em] text-[#7a7a7a] mb-1">DELIVERY MODE</label>
                <select value={form.delivery_mode} onChange={(e) => setForm({ ...form, delivery_mode: e.target.value })} className="w-full bg-[#0a0a0a] border border-[#1f1f1f] rounded-md px-3 py-2 text-sm outline-none focus:border-[#22c55e]">
                  <option value="auto">AUTO (from stock keys)</option>
                  <option value="manual">MANUAL (you deliver each)</option>
                </select>
              </div>
              {form.delivery_mode === 'manual' && (
                <div>
                  <label className="block text-[10px] tracking-[0.25em] text-[#7a7a7a] mb-1">MANUAL STOCK</label>
                  <input type="number" value={form.manual_stock} onChange={(e) => setForm({ ...form, manual_stock: e.target.value })} className="w-full bg-transparent border border-[#1f1f1f] rounded-md px-3 py-2 text-sm outline-none focus:border-[#22c55e]" />
                </div>
              )}
              <div className="col-span-2">
                <label className="block text-[10px] tracking-[0.25em] text-[#7a7a7a] mb-1">DESCRIPTION</label>
                <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="w-full bg-transparent border border-[#1f1f1f] rounded-md px-3 py-2 text-sm outline-none focus:border-[#22c55e] resize-none" />
              </div>
              <label className="col-span-2 flex items-center gap-2 text-[12px] text-[#8a8a8a]">
                <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active (visible on store)
              </label>
            </div>
            <button onClick={save} className="mt-5 w-full py-2.5 rounded-md text-[11px] tracking-[0.25em] bg-[#166534] hover:bg-[#15803d] text-[#e7ffef]"><Save size={12} className="inline mr-1" /> {form.id ? 'SAVE CHANGES' : 'CREATE PRODUCT'}</button>
          </div>

          {iconPickerOpen && (
            <IconPicker
              value={form.icon}
              onChange={(k) => setForm({ ...form, icon: k })}
              onClose={() => setIconPickerOpen(false)}
            />
          )}
        </div>
      )}

      {keysFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6 overflow-y-auto">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={closeKeys} />
          <div className="relative w-full max-w-[640px] border border-[#1f1f1f] bg-[#0a0a0a] rounded-md p-6 font-mono my-auto">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg text-[#e6e6e6]">Manage Stock Keys</h2>
              <button onClick={closeKeys} className="text-[#5a5a5a] hover:text-[#e6e6e6]"><X size={16} /></button>
            </div>
            <p className="text-[12px] text-[#8a8a8a] mb-4">{keysFor.name} — one key per line. When a customer buys, next unused key is auto-delivered.</p>
            <textarea value={keysText} onChange={(e) => setKeysText(e.target.value)} rows={5}
              placeholder={'user1@mail.com:Pass123\nuser2@mail.com:Pass456'}
              className="w-full bg-transparent border border-[#1f1f1f] rounded-md px-3 py-2 text-sm text-[#e6e6e6] outline-none focus:border-[#22c55e] resize-none" />
            <button onClick={addKeys} className="mt-3 px-4 py-2 rounded-md text-[11px] tracking-[0.25em] bg-[#166534] hover:bg-[#15803d] text-[#e7ffef]"><Plus size={11} className="inline mr-1" /> ADD KEYS</button>

            <div className="mt-5 border border-[#151515] rounded-md max-h-[240px] overflow-y-auto">
              <div className="grid grid-cols-[1fr_100px] px-3 py-2 text-[10px] tracking-[0.25em] text-[#5a5a5a] border-b border-[#151515] bg-[#080808]">
                <div>KEY</div><div className="text-right">STATUS</div>
              </div>
              {existingKeys.length === 0 && <div className="px-3 py-4 text-[12px] text-[#5a5a5a]">No keys yet.</div>}
              {existingKeys.map((k) => (
                <div key={k.id} className="grid grid-cols-[1fr_100px] px-3 py-2 text-[11px] border-b border-[#0f0f0f] last:border-0 items-center">
                  <code className="text-[#e6e6e6] truncate pr-2">{k.key}</code>
                  <div className={`text-right text-[10px] tracking-[0.2em] ${k.used ? 'text-[#8a8a8a]' : 'text-[#22c55e]'}`}>{k.used ? 'USED' : 'AVAILABLE'}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductsAdmin;
